// Shared "which other fields can this formula reference" logic for the two
// formula editors in the builder — FormulaBuilder (ordinary calculated
// fields) and CellFormulaBuilder (calculated table cells). Both need the same
// "this form" + "earlier forms in the procedure" picker shape, the same table
// cells and totals (TableRefPicker) and the same broken-reference detection;
// this is the one place that logic lives.
import { isCrossFormRef } from '@/lib/crossForm'
import {
  cellKey,
  formulaReaches,
  parseTableAggregateRef,
  resolveCell,
  tableRefLabels,
  type TableAggregateAxis,
  type TableAggregateOp,
} from '@/lib/tableForm'
import type { RefFormGroup } from './FormFieldRefSelect'
import type { FormField } from './types'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'
import type { TableCellType, TableConfig } from '@/types/cgt'
import type { CalcField } from '@/lib/formula'

// Field types usable as an operand. Numbers/calculated feed arithmetic;
// text-like fields feed string concatenation via the `+` operator.
export const OPERAND_TYPES: FormField['type'][] = [
  'number',
  'calculated',
  'text',
  'textarea',
  'date',
  'datetime',
  'select',
]

/** Labels for cross-form refs already present in a stored formula, "Label (Form)". */
export function buildCrossLabels(
  formula: string,
  crossForm: CrossFormAuthoring | undefined,
): Record<string, string> {
  const m: Record<string, string> = {}
  if (!crossForm) return m
  for (const match of formula.matchAll(/\{([^}]+)\}/g)) {
    const ref = match[1]
    if (!isCrossFormRef(ref)) continue
    const label = crossForm.labelForRef(ref)
    if (label) m[ref] = label
  }
  return m
}

/**
 * Hierarchical variable-picker groups: this form's eligible fields first,
 * then the forms earlier in the toolbar's template context (empty when the
 * cross-form context is off or the form has none).
 */
export function buildRefGroups({
  operandFields,
  currentFormName,
  crossForm,
}: {
  operandFields: { id: string; label: string; type: string }[]
  currentFormName?: string
  crossForm?: CrossFormAuthoring
}): RefFormGroup[] {
  const typeTag = (t: string) => (t === 'calculated' ? 'calc' : t === 'number' ? undefined : 'text')
  const self: RefFormGroup = {
    key: '',
    name: currentFormName ? `This form — ${currentFormName}` : 'This form',
    fields: operandFields.map(f => ({
      refKey: f.id,
      label: f.label || 'Untitled',
      type: typeTag(f.type === 'select' ? 'text' : f.type),
    })),
  }
  const upstream: RefFormGroup[] =
    crossForm?.available && crossForm.selectedProcedureTemplateId
      ? crossForm.upstreamForms.map(uf => ({
          key: uf.handle,
          name: uf.name,
          fields: uf.fields.map(f => ({
            refKey: f.refKey,
            label: f.label,
            type: typeTag(f.fieldType),
          })),
        }))
      : []
  return [self, ...upstream]
}

/** Every `{ref}` token in `formula` that resolves against neither known source. */
export function findBrokenRefs(formula: string, isKnown: (id: string) => boolean): string[] {
  return [...formula.matchAll(/\{([^}]+)\}/g)].map(m => m[1]).filter(id => !isKnown(id))
}

// ─── Table refs (z8nrz7eujb) ─────────────────────────────────────────────────

// Picker key for "a table on this form". Field ids are `[a-z0-9-]`, so the
// colon keeps it from ever colliding with a real ref key.
export const TABLE_REF = 'table:'

/** A table on this form whose cells and totals can be referenced. */
export interface TableSource {
  /** The table field's id; '' for a grid with no field, which has no totals. */
  fieldId: string
  label: string
  config: TableConfig
  /** The table whose cell is being edited (CellFormulaBuilder only). */
  current: boolean
}

export const tableRefKey = (t: TableSource) => `${TABLE_REF}${t.fieldId}`

/**
 * The tables a formula on this form can read: `current` first when given (the
 * live config of the table being designed, labelled "This table"), then every
 * other table field.
 */
export function buildTableSources(
  allFields: FormField[] | undefined,
  current?: { fieldId?: string; config: TableConfig },
): TableSource[] {
  const others = (allFields ?? [])
    .filter(f => f.type === 'table' && f.tableConfig && f.id !== current?.fieldId)
    .map(f => ({ fieldId: f.id, label: f.label || 'Table', config: f.tableConfig!, current: false }))
  return current
    ? [{ fieldId: current.fieldId ?? '', label: 'This table', config: current.config, current: true }, ...others]
    : others
}

/** One referenceable cell: its label, resolved type and the table it is in. */
export interface TableCellRef {
  label: string
  type: TableCellType
  /** Marked not applicable: it never holds a value. */
  disabled: boolean
  table: TableSource
}

/**
 * Every predefined cell of `tables` by cell key, labelled "Weight @ 2024" in
 * the current table and "Vitals › Weight @ 2024" in another.
 */
export function buildCellsByKey(tables: TableSource[]): Map<string, TableCellRef> {
  const m = new Map<string, TableCellRef>()
  for (const t of tables) {
    for (const col of t.config.columns) {
      for (const row of t.config.rows) {
        const key = cellKey(row.id, col.id)
        if (m.has(key)) continue
        const at = `${col.label || 'Column'} @ ${row.label || 'Row'}`
        const cell = resolveCell(t.config, row.id, col)
        m.set(key, {
          label: t.current ? at : `${t.label} › ${at}`,
          type: cell.type,
          disabled: cell.disabled,
          table: t,
        })
      }
    }
  }
  return m
}

// Cell types a formula may read, as cgt-backend's OPERAND_TYPES: an image or
// description cell holds no value, and checkbox / date-time cells are refused.
const OPERAND_CELL_TYPES: ReadonlySet<string> = new Set(['number', 'calculated', 'text', 'textarea', 'date', 'dropdown'])

/**
 * The label of every table cell and total a formula on this form may read, by
 * ref key, as `tableRefLabels` names them ("Vitals › Weight @ Day 1", "Sum of
 * Weight (Vitals)"). The one source of "is this table ref valid" for every
 * formula surface in the builder: a calculated field's chips and broken-ref
 * warning, and a gate's unknown-ref check. Matches what cgt-backend accepts:
 * a cell marked not applicable, or of a type that holds no usable value, is
 * left out, so a ref to it reads as unknown.
 */
export function tableFieldRefLabels(fields: FormField[]): Record<string, string> {
  const all = tableRefLabels(
    fields.map(f => ({ id: f.id, label: f.label, fieldType: f.type, tableConfig: f.tableConfig })),
  )
  const cells = buildCellsByKey(buildTableSources(fields))
  return Object.fromEntries(
    Object.entries(all).filter(([key]) => {
      const cell = cells.get(key)
      return !cell || (!cell.disabled && OPERAND_CELL_TYPES.has(cell.type))
    }),
  )
}

/**
 * The label of every total a formula can read, by token key: "Sum of Weight
 * (Vitals)" per column with number cells (the only column totals
 * cgt-backend accepts) and per row. A grid with no field id has none.
 */
export function buildTotalLabels(tables: TableSource[]): Record<string, string> {
  const all = tableRefLabels(
    tables.map(t => ({ id: t.fieldId, label: t.label, fieldType: 'table', tableConfig: t.config })),
  )
  return Object.fromEntries(Object.entries(all).filter(([key]) => parseTableAggregateRef(key)))
}

/**
 * Can a calculated field (`fieldId`) read this cell? Only a number or
 * calculated cell that is not marked not applicable and, if calculated, does
 * not already read the field through `calcNodes` (that would close a loop).
 */
export function calculatedFieldCanRead(
  cells: Map<string, TableCellRef>,
  calcNodes: CalcField[],
  fieldId: string,
  key: string,
): boolean {
  const cell = cells.get(key)
  if (!cell || cell.disabled || (cell.type !== 'number' && cell.type !== 'calculated')) return false
  return cell.type !== 'calculated' || !formulaReaches(calcNodes, key, fieldId)
}

/**
 * Labels of the cells in `formula` that are marked not applicable. Such a cell
 * never holds a value, so cgt-backend refuses a formula that reads it.
 */
export function notApplicableRefs(formula: string, cells: Map<string, TableCellRef>): string[] {
  const ids = [...new Set([...formula.matchAll(/\{([^}]+)\}/g)].map(m => m[1]))]
  return ids.filter(id => cells.get(id)?.disabled).map(id => cells.get(id)!.label)
}

/** What a table pick resolves to: one cell, or a column or row total. */
export type TablePickMode = 'cell' | `${TableAggregateAxis}:${TableAggregateOp}`

/**
 * Where to start the picker when it replaces an existing `{ref}` token: the
 * table the token reads, and its cell or total. A token naming no table cell
 * or total starts on `fallbackRef` (a field ref key, or a table).
 */
export function initialTablePick(
  token: string,
  tables: TableSource[],
  fallbackRef: string,
): { ref: string; mode: TablePickMode; colId: string; rowId: string } {
  const agg = parseTableAggregateRef(token)
  const aggTable = agg && tables.find(t => t.fieldId === agg.tableFieldId)
  if (agg && aggTable) {
    return {
      ref: tableRefKey(aggTable),
      mode: `${agg.axis}:${agg.op}`,
      colId: agg.axis === 'col' ? agg.id : '',
      rowId: agg.axis === 'row' ? agg.id : '',
    }
  }
  const sep = token.indexOf('__')
  if (sep > 0) {
    const rowId = token.slice(0, sep)
    const colId = token.slice(sep + 2)
    const cellTable = tables.find(
      t => t.config.columns.some(c => c.id === colId) && t.config.rows.some(r => r.id === rowId),
    )
    if (cellTable) return { ref: tableRefKey(cellTable), mode: 'cell', colId, rowId }
  }
  return { ref: fallbackRef, mode: 'cell', colId: '', rowId: '' }
}
